import {useEffect} from "react";
import {extendTheme, useColorMode, type ColorMode, type localStorageManager, type ThemeConfig} from "@chakra-ui/react";

// Chakra doesn't re-export its StorageManager interface.
type StorageManager = typeof localStorageManager;

// Also read by the pre-paint script in public/index.html.
const STORAGE_KEY = "tb-color-mode";
const DARK_QUERY = "(prefers-color-scheme: dark)";

/** The user's explicit light/dark choice; undefined means "follow the system". */
export function getSavedColorMode(): ColorMode | undefined {
    try {
        const value = localStorage.getItem(STORAGE_KEY);
        return value === "light" || value === "dark" ? value : undefined;
    } catch {
        return undefined;
    }
}

export function saveColorMode(mode: ColorMode | null) {
    try {
        if (mode) localStorage.setItem(STORAGE_KEY, mode);
        else localStorage.removeItem(STORAGE_KEY);
    } catch {
        // storage unavailable (private mode); the choice just won't persist
    }
}

// Chakra persists every resolved mode, which would freeze the system value on first visit; only explicit choices are saved.
export const colorModeManager: StorageManager = {
    type: "localStorage",
    ssr: false,
    get: (init?: ColorMode) => getSavedColorMode() ?? init,
    set: () => {},
};

/** Tracks OS light/dark switches while the user hasn't picked a mode explicitly. */
export function useFollowSystemColorMode() {
    const {setColorMode} = useColorMode();
    useEffect(() => {
        const query = window.matchMedia(DARK_QUERY);
        const onChange = () => {
            if (!getSavedColorMode()) setColorMode(query.matches ? "dark" : "light");
        };
        query.addEventListener("change", onChange);
        return () => query.removeEventListener("change", onChange);
    }, [setColorMode]);
}

const config: ThemeConfig = {
    initialColorMode: "system",
    useSystemColorMode: false,
};

export const theme = extendTheme({
    config,
    semanticTokens: {
        colors: {
            "surface": {default: "white", _dark: "gray.800"},
            "surface-muted": {default: "gray.50", _dark: "whiteAlpha.100"},
            "surface-subtle": {default: "gray.100", _dark: "gray.600"},
            "text-muted": {default: "gray.600", _dark: "gray.400"},
            "text-subtle": {default: "gray.500", _dark: "gray.400"},
            "border-subtle": {default: "gray.200", _dark: "whiteAlpha.200"},
        },
    },
});
